import * as React from "react";
import { observer } from "mobx-react-lite";
import { convertToHtml } from "mammoth";
import DOMPurify from "dompurify";
import { LoadingIndicator } from "src/modules/common/components/LoadingIndicator";
import DocumentViewVisualStore from "../stores/DocumentViewVisualStore";
import "./DocxViewer.less";

type Conversion = { file: Blob; html: string | null };

const DocxViewer: React.FC<{ store: DocumentViewVisualStore }> = ({
    store,
}) => {
    const file = store.fileBlob;
    const [conversion, setConversion] = React.useState<Conversion | null>(null);

    React.useEffect(() => {
        if (!file) {
            return;
        }

        let active = true;
        const reader = new FileReader();
        const fail = () => {
            if (active) {
                setConversion({ file, html: null });
            }
        };
        reader.onerror = fail;
        reader.onload = async () => {
            try {
                if (!(reader.result instanceof ArrayBuffer)) {
                    fail();
                    return;
                }

                const result = await convertToHtml(
                    { arrayBuffer: reader.result },
                    { includeDefaultStyleMap: true, externalFileAccess: false },
                );
                if (active) {
                    setConversion({
                        file,
                        html: DOMPurify.sanitize(result.value, {
                            USE_PROFILES: { html: true },
                        }),
                    });
                }
            } catch {
                fail();
            }
        };
        reader.readAsArrayBuffer(file);

        return () => {
            active = false;
            if (reader.readyState === FileReader.LOADING) {
                reader.abort();
            }
        };
    }, [file]);

    if (!file) {
        return null;
    }

    if (!conversion || conversion.file !== file) {
        return (
            <div role="status" aria-label="Loading document">
                <LoadingIndicator />
            </div>
        );
    }

    if (conversion.html === null) {
        return (
            <div className="error-message" role="alert">
                Failed to load document
            </div>
        );
    }

    return (
        <div className="docx-viewer">
            <div className="docx-content">
                <div
                    className="document-container"
                    dangerouslySetInnerHTML={{ __html: conversion.html }}
                />
            </div>
        </div>
    );
};

export default observer(DocxViewer);
